<template>
  <v-data-table-server
    v-model:page="page"
    :items-length="count"
    :items-per-page="25"
    :items-per-page-options="[{ value: 25, title: '25' }]"
    :hover="customers.length > 0"
    item-value="name"
    :headers="customerDataTableHeaders"
    :items="customers"
    :loading="loading"
    color="primary"
    sticky
    no-filter
    fixed-header
    density="default"
    height="calc(100% - 37px)"
    style="height: calc(100% - 93px); width: 100%"
    :row-props="generateRowProps"
  >
    <template #[`item.index`]="{ index }">
      {{ index + 1 }}
    </template>

    <template #[`item.full_name`]="{ item }">
      <div class="d-flex align-center">
        <v-avatar variant="tonal" color="primary" size="36" class="me-2">
          {{ item.getInitials() }}
        </v-avatar>
        <div class="d-flex flex-column">
          <h6
            class="text-body-2 font-weight-medium text-link"
            @click="emit('customer-selected', item)"
          >
            {{ item.getFullName() }}
          </h6>
        </div>
      </div>
    </template>

    <template #[`item.is_active`]="{ item }">
      <CustomerActiveStatusChip :is-active="item.is_active!" />
    </template>

    <template #[`item.email`]="{ item }">
      <a
        :href="`mailto:${item.email}`"
        target="_blank"
        rel="noopener noreferrer"
        class="font-weight-medium text-link"
      >
        {{ item.email }}
      </a>
    </template>

    <template #[`item.profile.site_assignments`]="{ value }">
      <div class="d-flex flex-column">
        <div
          v-for="(client, clientIndex) in getClientNames(value)"
          :key="clientIndex"
          class="font-weight-medium"
        >
          {{ client.name }}
        </div>
      </div>
    </template>

    <template #[`item.actions`]="{ item }">
      <CustomerDetailOptionsButton
        :customer="item"
        @customer-edit-pressed="(customer: ICustomer) => emit('customer-edit-pressed', customer)"
        @customer-delete-pressed="
          (customer: ICustomer) => emit('customer-delete-pressed', customer)
        "
        @customer-change-password-pressed="
          (customer: ICustomer) => emit('customer-change-password-pressed', customer)
        "
        @customer-change-sites-pressed="
          (customer: ICustomer) => emit('customer-change-sites-pressed', customer)
        "
        @customer-set-active-pressed="
          (customer: ICustomer) => emit('customer-set-active-pressed', customer)
        "
      />
    </template>

    <template v-slot:no-data>
      <NoDataAvailablePlaceholder
        header="No customers available"
        header-class="font-weight-medium text-h5"
      >
        <template #prepend>
          <v-card
            variant="flat"
            class="mask-bottom-card mb-3"
            color="transparent"
            width="180"
            height="120"
          >
            <v-list
              style="color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity))"
              variant="outlined"
              density="compact"
              lines="two"
              class="pt-0"
            >
              <v-list-item class="rounded-lg-xl mb-3 px-3">
                <template v-slot:prepend>
                  <v-avatar size="32" class="me-n1" color="green-darken-2 text-white">
                    JD
                  </v-avatar>
                </template>
                <v-skeleton-loader class="me-n3 ms-n4 my-n2" type="text, text" boilerplate />
              </v-list-item>
              <v-list-item class="rounded-lg-xl mb-3 px-3">
                <template v-slot:prepend>
                  <v-avatar size="32" class="me-n1" color="amber-darken-2 text-white">
                    DM
                  </v-avatar>
                </template>
                <v-skeleton-loader class="me-n3 ms-n4 my-n2" type="text, text" boilerplate />
              </v-list-item>
            </v-list>
          </v-card>
        </template>

        <template #description>
          <p class="mt-0 mb-6 text-caption text-medium-emphasis">
            <span v-if="currentUser?.hasPermission('customers.add_customer')">
              No customers available you can create a customer here</span
            >
            <span v-else>
              You do not have the permission to create a customer. Customer your administrator for
              more details
            </span>
          </p>
        </template>

        <template #primaryAction>
          <v-btn
            color="primary"
            depressed
            v-if="currentUser?.hasPermission('customers.add_customer')"
            @click="emit('customer-create-pressed')"
          >
            <template #prepend>
              <v-icon left class="hidden-sm-and-down"> {{ route.meta.icon }} </v-icon>
            </template>
            Create Customer
          </v-btn>
        </template>
      </NoDataAvailablePlaceholder>
    </template>
  </v-data-table-server>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import type { ICustomer } from '@/models/customer'
import { useAuthStore } from '@/stores'
import type { IClient } from '@/models/client'
import type { ICustomerSiteAssignment } from '@/models/site-management'

import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue'
import CustomerActiveStatusChip from './CustomerActiveStatusChip.vue'
import CustomerDetailOptionsButton from './CustomerDetailOptionsButton.vue'

interface Props {
  customers: ICustomer[]
  count: number
  loading: boolean
}

interface Emit {
  (e: 'customer-selected', customer: ICustomer): void
  (e: 'customer-create-pressed'): void
  (e: 'customer-edit-pressed', customer: ICustomer): void
  (e: 'customer-delete-pressed', customer: ICustomer): void
  (e: 'customer-change-password-pressed', customer: ICustomer): void
  (e: 'customer-change-sites-pressed', customer: ICustomer): void
  (e: 'customer-set-active-pressed', customer: ICustomer): void
}

const page = defineModel<number>('page')

const emit = defineEmits<Emit>()
defineProps<Props>()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const route = useRoute()

const customerDataTableHeaders = ref<any[]>([
  {
    title: '#',
    align: 'start',
    key: 'index',
    width: 20,
    sortable: false
  },
  {
    title: 'Name',
    align: 'start',
    key: 'full_name',
    sortable: false
  },
  {
    title: 'Email',
    align: 'start',
    key: 'email',
    sortable: false
  },
  {
    title: 'Title',
    align: 'start',
    key: 'profile.title',
    sortable: false
  },
  {
    title: 'Client',
    align: 'start',
    key: 'profile.site_assignments',
    sortable: false
  },
  {
    title: 'Status',
    align: 'start',
    key: 'is_active',
    sortable: false
  },

  { title: '', key: 'actions', align: 'center', sortable: false }
])

function generateRowProps(props: { index: number; item: ICustomer }) {
  return {
    // apply click listener to row props to open detail drawer
    onClick: () => emit('customer-selected', props.item)
  }
}

function getClientNames(value: ICustomerSiteAssignment[]): IClient[] {
  // Get unique client names from the site assignments
  return value.reduce((clientNames: IClient[], siteAssignment) => {
    const client = siteAssignment.client
    if (client && !clientNames.some((c) => c.id === client.id)) {
      clientNames.push(client)
    }
    return clientNames
  }, [])
}
</script>
